import {
    EMPTY_VALUES,
    appendSanitizedString,
    errorMsg,
    getDateFormat,
    getTimeFormat,
    isNotEmpty,
    isPositiveNumber,
    sanitizeString
} from "../../../../Apis/functions";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {ApprovalMaterialRequest} from "../../../../reduxToolkit/MaterialRequest/MaterialRequestApi";
import {toast} from "react-toastify";
import {getToastMessageByStatus} from "../../ApprovalTracker/Model/Model";
import i18next from "i18next";
import {
    getPhotoPicker, normalizePhotoPickerFile
} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import {MIME_TYPE} from "../../../../utils/Base64ToBlob";
import {permissionObj} from "../../../../Helpers/permission";
import {
    IconArrowEntry,
    IconArrowExit,
    IconArrowRightFromLine,
    IconArrowRightToLine,
    IconCircleCross,
    IconDocumentStaggeredLines,
    IconExitDevice,
    IconGuardTitle,
    IconPackageReturn, IconPoultryTransportVehicle,
    IconSwapHorizontal,
    IconVehicle,
    IconVehicleArrowArrive,
    IconVehicleArrowReturn,
    IconVehicleInMotion,
    PersonOutlinedChip
} from "../../../../components/buttons/IconButtons";
import {FILE_MODEL} from "../../Visitor/Visitor/Model/Model";
import QRCode from "qrcode";
import {GetMaterialControlQrCode} from "../../../../reduxToolkit/MaterialControl/MaterialControlApi";


export const MATERIAL_CONTROL_TABS = {
    ENTRY_AND_EXIT_FORMS: {
        INDEX: 0,
        TRANS: "entry_and_exit_forms",
    },
    VEHICLE_MOVEMENTS: {
        INDEX: 1,
        TRANS: "vehicle_movements",
    },
    REQUESTS: {
        INDEX: 2,
        TRANS: "requests",
    }
}

/**
 * Sub views of the material control tabs.
 *
 * The requests tab splits the requests that are still in course from the ones
 * that already ended, so each sub view reads its own table model.
 */
export const SUB_VIEW_MODEL = {
    REQUESTS: {
        REQUEST: "REQUEST",
        HISTORY: "HISTORY",
    }
}

/**
 * Permission each tab of the material control is read with.
 *
 * Every tab is a module of its own, so the tab is displayed with the read
 * permission of the module that serves it.
 */
export const TAB_READ_PERMISSION_MATERIAL_CONTROL = {
    [MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX]: permissionObj?.WEB_ENTRY_AND_EXIT_FORMS_READ,
    [MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX]: permissionObj?.WEB_VEHICLE_MOVEMENTS_READ,
    [MATERIAL_CONTROL_TABS.REQUESTS.INDEX]: permissionObj?.WEB_MATERIAL_CONTROL_REQUEST_READ,
}

/**
 * Permission each tab of the material control registers with.
 *
 * The requests tab only reads and exports the folios registered from the entry
 * and exit forms and the vehicle movements, so it declares no create permission
 * and the create action is not offered there.
 */
export function TAB_PERMISSION_MATERIAL_CONTROL (subView){
    return {
        CREATE: {
            [MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX]: permissionObj?.WEB_ENTRY_AND_EXIT_FORMS_CREATE,
            [MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX]: permissionObj?.WEB_VEHICLE_MOVEMENTS_CREATE,
        },
        // The table of a tab is exported by the module that lists it, so only
        // the tabs that declare an export permission carry the action: the
        // requests tab exports the table of the sub view it is read with, the
        // requested forms or their history
        EXPORT: {
            [MATERIAL_CONTROL_TABS.ENTRY_AND_EXIT_FORMS.INDEX]: permissionObj?.WEB_ENTRY_AND_EXIT_FORMS_EXPORT,
            [MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX]: permissionObj?.WEB_VEHICLE_MOVEMENTS_EXPORT,
            [MATERIAL_CONTROL_TABS.REQUESTS.INDEX]: permissionObj?.WEB_MATERIAL_CONTROL_REQUEST_EXPORT,
        }
    }
}

/**
 * Type each entry and exit form is registered with.
 *
 * The service stores the type as text, so it is the value that tells one form
 * from another once the folio is read back.
 */
export const ENTRY_AND_EXIT_FORM_TYPE = {
    GENERAL_ENTRY_EXIT: 'GENERAL_ENTRY_EXIT',
    PLANT_MATERIAL_CONTROL: 'PLANT_MATERIAL_CONTROL',
    CRATES_PALLETS_RETURN: 'CRATES_PALLETS_RETURN',
    POULTRY_TRUCK_EXIT: 'POULTRY_TRUCK_EXIT',
    LIVE_POULTRY_TRUCK: 'LIVE_POULTRY_TRUCK'
};

/**
 * Type a movement of a vehicle is registered with.
 *
 * The movement is not registered with a form of entry and exit, so it travels
 * with a type of its own, which is the one the vehicle movements module lists.
 */
export const VEHICLE_MOVEMENT_TYPE = 'VEHICLE_MOVEMENT';

/**
 * Material request item model.
 *
 * Represents an individual material item requested by the user.
 * Includes validation logic for required item fields.
 */
export const GENERAL_ENTRY_EXIT_ITEM_MODEL = {
    id: null,
    name: '',
    quantity: '',
    measurementUnitId: null,

    isValid() {
        return (
            isNotEmpty(this.name) &&
            isPositiveNumber(this.quantity) &&
            this.measurementUnitId !== null
        );
    }
};

/**
 * Builds an empty item of an entry and exit form.
 *
 * The item receives a unique id so the list keeps identifying it while it is
 * edited or deleted.
 *
 * @param {Object} itemModel - Item model of the form.
 *
 * @returns {Object} New empty item.
 */
export const buildEntryAndExitFormItem = (itemModel) => ({
    ...itemModel,
    id: `${Date.now()}-${Math.random()}`
});

/**
 * Copies a value of a form model so the form being captured never writes on it.
 *
 * The models are declared once and shared by every form that is registered, so
 * the lists and the blocks they hold are copied and only the plain values and
 * the functions of the model travel as they are.
 *
 * @param {*} value - Value declared by the model.
 *
 * @returns {*} Value the form can write on.
 */
const copyFormValue = (value) => {
    if (Array.isArray(value))
        return value.map(copyFormValue);

    if (value !== null && typeof value === 'object')
        return { ...value };

    return value;
};

/**
 * Builds a form out of the model of its type.
 *
 * Every item receives its own id, so the list keeps identifying the ones that
 * the form starts with while they are edited or deleted.
 *
 * @param {Object} formModel - Model of the form being registered.
 *
 * @returns {Object} Form the user captures.
 */
export const buildEntryAndExitForm = (formModel) => {
    const form = Object.fromEntries(
        Object.entries(formModel).map(([name, value]) => [name, copyFormValue(value)])
    );

    if (Array.isArray(form.items))
        form.items = form.items.map(buildEntryAndExitFormItem);

    return form;
};

/**
 * Movement direction of a general entry and exit form.
 *
 * Determines which sections of the form are enabled:
 * - ENTRY enables the departure and entry sections.
 * - EXIT enables the arrival and exit sections.
 */
export const MOVEMENT_DIRECTION = {
    ENTRY: 1,
    EXIT: 2
};

/**
 * UI configuration for each movement direction option.
 *
 * Used to render the selectable direction cards and the header
 * of the section enabled by the current selection.
 */
export const MOVEMENT_DIRECTION_OPTIONS = [
    {
        key: MOVEMENT_DIRECTION.ENTRY,
        value: 'ENTRY',
        trans: 'entry',
        dateName: 'entryDate',
        dateTrans: 'entry_date_and_time',
        description: 'material_arrives_at_the_plant',
        Icon: IconArrowRightToLine,
        iconBackground: 'background-success-100',
        iconColor: 'icon-color-success-700'
    },
    {
        key: MOVEMENT_DIRECTION.EXIT,
        value: 'EXIT',
        trans: 'exit',
        dateName: 'exitDate',
        dateTrans: 'exit_date_and_time',
        description: 'material_leaves_the_plant',
        Icon: IconArrowRightFromLine,
        iconBackground: 'background-warning-100',
        iconColor: 'icon-color-warning-700'
    }
];

/**
 * Returns the direction option configuration matching the given key.
 *
 * @param {number} movementDirection - Selected movement direction.
 *
 * @returns {Object|null} Matching option or null when nothing is selected.
 */
export const getMovementDirectionOption = (movementDirection) =>
    MOVEMENT_DIRECTION_OPTIONS.find((e) => e.key === movementDirection) || null;

/**
 * Returns the direction option configuration matching the direction a folio
 * was registered with.
 *
 * The service stores the direction as text, so a folio that is read back is
 * matched by the value it was registered with and not by the key the forms
 * select it with.
 *
 * @param {string} movementDirection - Direction the folio carries.
 *
 * @returns {Object|null} Matching option or null when the folio carries none.
 */
export const getMovementDirectionOptionByValue = (movementDirection) =>
    MOVEMENT_DIRECTION_OPTIONS.find((e) => e.value === movementDirection) || null;

/**
 * Default values of the date captured for each movement direction.
 *
 * Exposed on its own so the date of both directions can be restored whenever
 * the user changes the movement direction and only the date that belongs to
 * the current direction is submitted.
 */
export const MOVEMENT_DIRECTION_DATE_DEFAULTS = {
    entryDate: null,
    exitDate: null
};

/**
 * Appends the date captured for the selected movement direction to the request payload.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} model - Form that captures the movement direction.
 *
 * @returns {void}
 */
export const appendMovementDirectionDate = (formData, model) => {
    const option = getMovementDirectionOption(model.movementDirection);

    if (!option || model[option.dateName] === null)
        return;

    formData.append('expectedMovementDate', new Date(model[option.dateName]).getTime());
};

/**
 * Appends the direction of the movement to the request payload.
 *
 * The service stores the direction as text, so the option of the form is the
 * one that says which value it is registered with.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} model - Form that captures the movement direction.
 *
 * @returns {void}
 */
export const appendMovementDirection = (formData, model) => {
    const option = getMovementDirectionOption(model.movementDirection);

    if (!option)
        return;

    formData.append('movementDirection', option.value);
};

/**
 * Configuration of the complementary section rendered for each movement direction.
 *
 * An entry movement is complemented by the departure of the vehicle and an exit
 * movement by its arrival, so both sections share the same structure and only
 * differ in the model fields they write and the labels they display.
 */
export const MOVEMENT_DIRECTION_SECTIONS = {
    [MOVEMENT_DIRECTION.ENTRY]: {
        idPrefix: 'ges_departure',
        gateEventType: 'DEPARTURE',
        sectionTitle: 'departure',
        Icon: IconVehicleArrowArrive,
        title: 'departure_required',
        description: 'enable_this_if_the_vehicle_will_leave_the_plant_after_the_material_entry_the_guard_will_register_its_departure_at_the_gate_after_completing_the_entry',
        checkboxTrans: 'vehicle_will_leave_empty',
        placeholder: 'add_any_relevant_details_about_the_vehicles_departure_or_the_material_it_takes_out_of_the_plant',
        requiredName: 'departureRequired',
        checkboxName: 'vehicleWillLeaveEmpty',
        detailsName: 'departureDetails'
    },
    [MOVEMENT_DIRECTION.EXIT]: {
        idPrefix: 'ges_arrival',
        gateEventType: 'ARRIVAL',
        sectionTitle: 'arrival',
        Icon: IconVehicleArrowReturn,
        title: 'arrival_required',
        description: 'enable_this_if_the_vehicle_needs_to_enter_the_plant_before_the_material_exit_the_guard_will_register_its_arrival_at_the_gate_before_completing_the_exit',
        checkboxTrans: 'vehicle_will_arrive_empty',
        placeholder: 'add_any_relevant_details_about_the_vehicles_arrival_or_the_material_it_brings_into_the_plant',
        requiredName: 'arrivalRequired',
        checkboxName: 'vehicleWillArriveEmpty',
        detailsName: 'arrivalDetails'
    }
};

/**
 * Returns the section configuration matching the given movement direction.
 *
 * @param {number} movementDirection - Selected movement direction.
 *
 * @returns {Object|null} Matching section or null when nothing is selected.
 */
export const getMovementDirectionSection = (movementDirection) =>
    MOVEMENT_DIRECTION_SECTIONS[movementDirection] || null;

/**
 * Validates a complementary section of the vehicle movement.
 *
 * The section is valid when it is not required, when the vehicle comes or goes
 * empty, or when the details of the movement are captured.
 *
 * @param {Object} model - Form that captures the section.
 * @param {Object|null} section - Section configuration being validated.
 *
 * @returns {boolean} Returns true when the section has information.
 */
export const isValidSection = (model, section) => (
    section !== null &&
    (!model[section.requiredName] || model[section.checkboxName] || isNotEmpty(model[section.detailsName]))
);

/**
 * Validates the complementary section enabled by the movement direction.
 *
 * @param {Object} model - Form that captures the movement direction.
 *
 * @returns {boolean} Returns true when the selected section has information.
 */
export const isValidMovementDirectionSection = (model) =>
    isValidSection(model, getMovementDirectionSection(model.movementDirection));

/**
 * Appends a complementary section of the vehicle movement to the request payload.
 *
 * The details of the movement are only sent when the section is required and
 * the vehicle does not come or go empty.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} model - Form that captures the section.
 * @param {Object|null} section - Section configuration being sent.
 *
 * @returns {void}
 */
export const appendSection = (formData, model, section) => {
    if (!section)
        return;

    formData.append('gateRequirements[0].gateEventType', section.gateEventType);
    formData.append('gateRequirements[0].required', model[section.requiredName]);

    if (model[section.requiredName]) {
        formData.append('gateRequirements[0].arriveEmpty', model[section.checkboxName]);
        appendSanitizedString(formData, 'gateRequirements[0].notes', model[section.detailsName]);
    }
};

/**
 * Appends the complementary section enabled by the movement direction to the
 * request payload.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} model - Form that captures the movement direction.
 *
 * @returns {void}
 */
export const appendMovementDirectionSection = (formData, model) => {
    appendSection(formData, model, getMovementDirectionSection(model.movementDirection));
};

/**
 * Default values of the departure section.
 */
export const DEPARTURE_SECTION_DEFAULTS = {
    departureRequired: false,
    vehicleWillLeaveEmpty: false,
    departureDetails: ''
};

/**
 * Default values of the arrival section.
 */
export const ARRIVAL_SECTION_DEFAULTS = {
    arrivalRequired: false,
    vehicleWillArriveEmpty: false,
    arrivalDetails: ''
};

/**
 * Default values of the departure and arrival sections.
 *
 * Exposed on its own so the fields of both sections can be restored
 * whenever the user changes the movement direction.
 */
export const MOVEMENT_DIRECTION_SECTION_DEFAULTS = {
    ...DEPARTURE_SECTION_DEFAULTS,
    ...ARRIVAL_SECTION_DEFAULTS
};

/**
 * Section rendered by the forms that do not let the user choose the movement
 * direction.
 *
 * Those forms always register an exit of the plant, which is the movement
 * complemented by the arrival of the vehicle.
 */
export const FIXED_ARRIVAL_SECTION = getMovementDirectionSection(MOVEMENT_DIRECTION.EXIT);

/**
 * Direction registered by the forms that always take the material out of the
 * plant, the ones that do not let the user choose it.
 */
export const FIXED_MOVEMENT_DIRECTION = getMovementDirectionOption(MOVEMENT_DIRECTION.EXIT).value;

/**
 * Validates the people and departments involved in an entry and exit form.
 *
 * Only the departments responsible for the movement are asked for. The employee
 * that requests the form is optional, because a user that registers a form of
 * its own is the one that requests it, and so are the watchers that view it.
 *
 * @param {Array} responsibleDepartments - Departments responsible for the movement.
 *
 * @returns {boolean} Returns true when the departments have information.
 */
export const isValidEntryAndExitFormPeople = ({ responsibleDepartments }) => (
    responsibleDepartments?.length > 0
);

/**
 * Appends the people and departments involved in an entry and exit form
 * to the request payload.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Array} selectedEmployees - Employee that requests the form.
 * @param {Array} watchers - Employees that can view the form.
 * @param {Array} responsibleDepartments - Departments responsible for the movement.
 *
 * @returns {void}
 */
export const appendEntryAndExitFormPeople = (formData, { selectedEmployees, watchers, responsibleDepartments }) => {
    // A form with no requester chosen is asked for by the user that registers
    // it, so the service completes it with the one that wrote it
    if (selectedEmployees?.length > 0)
        formData.append('requestedById', selectedEmployees[0]?.userId);

    watchers?.forEach((e, index) => {
        formData.append(`watchers[${index}].userId`, e.userId);
    });

    responsibleDepartments?.forEach((e, index) => {
        formData.append(`departments[${index}].departmentId`, e.id);
    });
};

/**
 * Appends the evidence of an entry and exit form to the request payload.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Array|Object} files - Files picked at the gate, or the ones already
 * registered, which only travel with their id.
 *
 * @returns {void}
 */
export const appendEntryAndExitFormEvidence = (formData, files) => {
    const list = (Array.isArray(files) ? files : (files ? [files] : []))
        .filter((e) => e?.fileSelected || e?.fileId);

    list.forEach((e, index) => {
        // A file already registered is referenced by its id, because the one
        // that travels with its content is the one picked at the gate
        if (!e?.fileSelected) {
            formData.append(`evidence[${index}].fileUploadDto.id`, e.fileId);
            return;
        }

        formData.append(`evidence[${index}].fileUploadDto.files`, e.fileSelected);
        formData.append(`evidence[${index}].fileUploadDto.userFileNames`, e.fileSelected.name);
    });
};

/**
 * Appends the transport of an entry and exit form to the request payload.
 *
 * The transport is a block of its own in the service, so every field of the
 * vehicle travels under it, and the ones the requester left to the guard are
 * not sent.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} fields - Fields of the transport, named as the service reads them.
 *
 * @returns {void}
 */
export const appendEntryAndExitFormTransport = (formData, fields) => {
    Object.entries(fields).forEach(([name, value]) => {
        const sanitized = sanitizeString(value);

        if (sanitized)
            formData.append(`transport.${name}`, sanitized);
    });
};

export const GENERAL_ENTRY_EXIT_FORM_MODEL = {
    id: null,
    title: '',
    code: '',
    requestedById: null,
    vehicleInformation: '',
    movementDirection: null,
    expectedDateAndTime: null,
    ...MOVEMENT_DIRECTION_SECTION_DEFAULTS,
    items: [GENERAL_ENTRY_EXIT_ITEM_MODEL],
    evidence: [],
    isValid({ photoPicker, selectedEmployees, watchers, responsibleDepartments }) {
        return (
            isNotEmpty(this.title) &&
            isNotEmpty(this.code) &&
            isValidMovementDirectionSection(this) &&
            this.expectedDateAndTime !== null &&
            isValidEntryAndExitFormPeople({ selectedEmployees, watchers, responsibleDepartments }) &&
            photoPicker?.listFiles?.length > 0
        );
    },
    isValidItems() {
        return this.items.filter(e => !e.isValid()).length === 0;
    },
    toRegister({ selectedEmployees, watchers, responsibleDepartments, files }) {
        const formData = new FormData();
        formData.append('type', ENTRY_AND_EXIT_FORM_TYPE.GENERAL_ENTRY_EXIT);
        formData.append('title', sanitizeString(this.title));
        formData.append('code', sanitizeString(this.code));
        appendEntryAndExitFormPeople(formData, { selectedEmployees, watchers, responsibleDepartments });
        appendMovementDirection(formData, this);
        formData.append('expectedMovementDate', new Date(this.expectedDateAndTime).getTime());
        appendEntryAndExitFormTransport(formData, { vehicleInformation: this.vehicleInformation });

        appendMovementDirectionSection(formData, this);
        appendEntryAndExitFormEvidence(formData, files);

        this.items.forEach((e, index) => {
            formData.append(`completeItems[${index}].name`, sanitizeString(e.name));
            formData.append(`completeItems[${index}].quantity`, e.quantity);
            formData.append(`completeItems[${index}].measurementUnitId`, e.measurementUnitId);
        });
        return formData;
    },
};
export const PLANT_MATERIAL_CONTROL_TYPE_TABS = [
    {
        id: 0,
        key: 'OUT',
        translation: 'out',
        icon: <IconArrowRightFromLine className='h-18px w-18px' />,
    },
    {
        id: 1,
        key: 'IN',
        translation: 'in',
        icon: <IconArrowRightToLine className='h-18px w-18px' />,
    }
];

/**
 * Returns the direction a material of a plant material control was captured
 * with, and null for the materials that are read with a quantity instead.
 *
 * @param {string} type - Direction the material takes.
 *
 * @returns {Object|null} Tab the direction is read with.
 */
export const getMaterialControlItemTypeTab = (type) =>
    PLANT_MATERIAL_CONTROL_TYPE_TABS.find((e) => e.key === type) || null;

export const PLANT_MATERIAL_CONTROL_ITEM_MODEL = {
    id: null,
    type: null,
    name: '',
    isValid() {
        return (
            isNotEmpty(this.name) &&
            this.type !== null
        );
    }
};

/**
 * Tells whether nothing was captured in a material of a plant material control.
 *
 * The materials are written at the gate when the requester leaves them blank,
 * so a material is empty only while nothing was written in it: the movement it
 * comes or goes with says that it was started the same way its description
 * does, and a material that was started is one that is missing information.
 *
 * @param {Object} item - Material being read.
 *
 * @returns {boolean} Returns true when the material is empty.
 */
export const isEmptyPlantMaterialControlItem = (item) =>
    !isNotEmpty(item?.name) && (item?.type === null || item?.type === undefined);

/**
 * Tells whether a material of a plant material control can stay as it is.
 *
 * A folio is registered and closed with no materials at all, so the only
 * material of the list is the one that can be left blank; once a second one is
 * added the list says that the materials were being captured, so every one of
 * them has to be complete.
 *
 * @param {Array} items - Materials of the folio.
 * @param {Object} item - Material being read.
 *
 * @returns {boolean} Returns true when the material is not asked for.
 */
export const isOptionalPlantMaterialControlItem = (items, item) =>
    (items || []).length === 1 && isEmptyPlantMaterialControlItem(item);

/**
 * Tells whether the materials of a plant material control are complete.
 *
 * @param {Array} items - Materials of the folio.
 *
 * @returns {boolean} Returns true when every material that was started is complete.
 */
export const areValidPlantMaterialControlItems = (items) =>
    (items || []).every((e) => isOptionalPlantMaterialControlItem(items, e) || e.isValid());

/**
 * Information shared by the entry and exit forms that only capture the request
 * data, the vehicle and the list of materials.
 *
 * Each form type builds its own model from this one so the fields that belong
 * to a single type are not sent by the rest of the forms.
 */
const PLANT_MATERIAL_CONTROL_BASE_MODEL = {
    id: null,
    title: '',
    code: '',
    requestedById: null,
    vehicleInformation: '',
    evidence: [],
    /**
     * The evidence of the movement is attached at the gate, so the folio is
     * registered with the request data and the people it involves.
     */
    isValid({ selectedEmployees, watchers, responsibleDepartments }) {
        return (
            isNotEmpty(this.title) &&
            isNotEmpty(this.code) &&
            isValidEntryAndExitFormPeople({ selectedEmployees, watchers, responsibleDepartments })
        );
    },
    /**
     * Forms that capture fixed quantities instead of a list of materials do not
     * define the items, so there is nothing to validate for them.
     */
    isValidItems() {
        return !this.items || this.items.filter(e => !e.isValid()).length === 0;
    },
    toRegister({ selectedEmployees, watchers, responsibleDepartments, files }) {
        const formData = new FormData();
        formData.append('type', this.materialControlType);
        formData.append('title', sanitizeString(this.title));
        formData.append('code', sanitizeString(this.code));
        appendEntryAndExitFormPeople(formData, { selectedEmployees, watchers, responsibleDepartments });
        appendEntryAndExitFormTransport(formData, { vehicleInformation: this.vehicleInformation });
        appendEntryAndExitFormEvidence(formData, files);

        // A material the requester left blank is written at the gate, so only
        // the ones that were captured travel with the folio
        this.items
            ?.filter((e) => isNotEmpty(e.name) && e.type !== null)
            .forEach((e, index) => {
                formData.append(`basicItems[${index}].description`, sanitizeString(e.name));
                formData.append(`basicItems[${index}].type`, e.type);
            });
        return formData;
    },
};

/**
 * Plant material control form.
 *
 * Complements the base information with the direction of the movement and the
 * section it enables, so the guard knows which movements are registered at the
 * gate.
 */
export const PLANT_MATERIAL_CONTROL_FORM_MODEL = {
    ...PLANT_MATERIAL_CONTROL_BASE_MODEL,
    materialControlType: ENTRY_AND_EXIT_FORM_TYPE.PLANT_MATERIAL_CONTROL,
    carrierCompany: '',
    movementDirection: null,
    ...MOVEMENT_DIRECTION_DATE_DEFAULTS,
    ...MOVEMENT_DIRECTION_SECTION_DEFAULTS,
    items: [PLANT_MATERIAL_CONTROL_ITEM_MODEL],
    /**
     * The transport and the date of the movement are completed by the guard at
     * the gate, so the folio is registered with the direction of the movement
     * and the section it enables.
     */
    isValid(payload) {
        return (
            PLANT_MATERIAL_CONTROL_BASE_MODEL.isValid.call(this, payload) &&
            isValidMovementDirectionSection(this)
        );
    },
    /**
     * The materials are written at the gate when the requester leaves them
     * blank, so a folio is registered with none of them; the material that was
     * started is the one that has to be complete.
     */
    isValidItems() {
        return areValidPlantMaterialControlItems(this.items);
    },
    toRegister(payload) {
        const formData = PLANT_MATERIAL_CONTROL_BASE_MODEL.toRegister.call(this, payload);
        appendEntryAndExitFormTransport(formData, { carrierCompany: this.carrierCompany });
        appendMovementDirection(formData, this);
        appendMovementDirectionDate(formData, this);
        appendMovementDirectionSection(formData, this);
        return formData;
    },
};

/**
 * Fields of the transport that the guard can complete at the gate.
 *
 * They are optional for the requester, so they are only sent when they were
 * captured and the guard is not forced to overwrite an empty value. Each form
 * captures its own transport, so it declares the fields it leaves to the guard.
 */
const CRATES_AND_PALLETS_TRANSPORT_FIELDS = ['tractor', 'trailer', 'driversName', 'sealNumber'];

const POULTRY_TRUCK_TRANSPORT_FIELDS = ['tractor', 'dollyNo', 'driversName', 'licenseNo'];

/**
 * Name the service reads each transport field with, for the ones the form
 * names differently.
 */
const TRANSPORT_FIELD_NAMES = {
    driversName: 'driverName'
};

/**
 * Appends the transport fields that the requester left to the guard.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} model - Form that captures the transport.
 * @param {Array} fields - Names of the fields left to the guard.
 *
 * @returns {void}
 */
const appendOptionalTransportFields = (formData, model, fields) => {
    const transport = {};

    fields.forEach((name) => {
        transport[TRANSPORT_FIELD_NAMES[name] || name] = model[name];
    });

    appendEntryAndExitFormTransport(formData, transport);
};

/**
 * Crates and pallets return form.
 *
 * Complements the base information with the transport that returns the crates
 * and with the quantities expected at the gate, so the guard can confirm what
 * arrives at the plant.
 */
/**
 * Packaging the crates and pallets return counts, named as the service stores
 * every one of them.
 */
const CRATES_AND_PALLETS_PACKAGING = [
    { name: 'largeBoxes', packagingType: 'LARGE_BOX' },
    { name: 'smallBoxes', packagingType: 'SMALL_BOX' },
    { name: 'lids', packagingType: 'LID' },
    { name: 'pallets', packagingType: 'PALLET' }
];

export const CRATES_AND_PALLETS_RETURN_FORM_MODEL = {
    ...PLANT_MATERIAL_CONTROL_BASE_MODEL,
    materialControlType: ENTRY_AND_EXIT_FORM_TYPE.CRATES_PALLETS_RETURN,
    carrierCompany: '',
    tractor: '',
    trailer: '',
    driversName: '',
    sealNumber: '',
    largeBoxes: '',
    smallBoxes: '',
    lids: '',
    pallets: '',
    ...ARRIVAL_SECTION_DEFAULTS,
    /**
     * The transport and the quantities that come back are completed by the
     * guard at the gate, so the folio is registered with the arrival of the
     * vehicle alone.
     */
    isValid(payload) {
        return (
            PLANT_MATERIAL_CONTROL_BASE_MODEL.isValid.call(this, payload) &&
            isValidSection(this, FIXED_ARRIVAL_SECTION)
        );
    },
    toRegister(payload) {
        const formData = PLANT_MATERIAL_CONTROL_BASE_MODEL.toRegister.call(this, payload);
        formData.append('movementDirection', FIXED_MOVEMENT_DIRECTION);
        appendEntryAndExitFormTransport(formData, { carrierCompany: this.carrierCompany });

        // Every packaging travels with the folio, because the guard counts all
        // of them at the gate; a quantity the requester left blank is the one
        // that is not expected yet, so it is registered without it
        CRATES_AND_PALLETS_PACKAGING.forEach(({ name, packagingType }, index) => {
            formData.append(`packagingQuantities[${index}].packagingType`, packagingType);

            if (isNotEmpty(this[name]))
                formData.append(`packagingQuantities[${index}].expectedQuantity`, this[name]);
        });

        appendOptionalTransportFields(formData, this, CRATES_AND_PALLETS_TRANSPORT_FIELDS);
        appendSection(formData, this, FIXED_ARRIVAL_SECTION);
        return formData;
    },
};

/**
 * Default values of a thermo trailer of the poultry truck exit form.
 */
export const THERMO_TRAILER_MODEL = {
    destination: '',
    thermoNo: '',
    seal1No: '',
    seal2No: '',
    avisoCzmNo: '',
    dispatchNoteNo: '',
    notes: ''
};

/**
 * Name the service reads each field of a thermo trailer with, for the ones the
 * form names differently.
 */
const THERMO_TRAILER_FIELD_NAMES = {
    avisoCzmNo: 'avisoCzm'
};

/**
 * Thermo trailers captured by the poultry truck exit form.
 *
 * The truck always leaves with a first trailer and can pull a second one, so
 * both are captured with the same fields and are only told apart by the model
 * field that stores them, by their title and by whether they have to be
 * completed: the first trailer always leaves the plant, so every one of its
 * fields is required at the gate, while the second one is optional.
 */
export const THERMO_TRAILER_SECTIONS = [
    {
        idPrefix: 'pte_thermo_trailer_1',
        title: 'thermo_trailer_1',
        fieldName: 'thermoTrailer1',
        required: true
    },
    {
        idPrefix: 'pte_thermo_trailer_2',
        title: 'thermo_trailer_2_optional',
        fieldName: 'thermoTrailer2',
        required: false
    }
];

/**
 * Appends the thermo trailers pulled by the truck to the request payload.
 *
 * Every field of a trailer can be completed by the guard at the gate, so only
 * the ones that were captured are sent and an empty trailer is not registered.
 * The service reads the trailers as a list, so the ones that travel are
 * numbered one after another and the trailer they describe is told by its
 * position, which is the one of the section that captured it.
 *
 * @param {FormData} formData - Payload of the form being registered.
 * @param {Object} model - Form that captures the thermo trailers.
 *
 * @returns {void}
 */
const appendThermoTrailers = (formData, model) => {
    let index = 0;

    THERMO_TRAILER_SECTIONS.forEach((section, position) => {
        const trailer = model[section.fieldName];

        const values = Object.keys(THERMO_TRAILER_MODEL)
            .map((name) => [THERMO_TRAILER_FIELD_NAMES[name] || name, sanitizeString(trailer?.[name])])
            .filter(([, value]) => value);

        if (values.length === 0)
            return;

        formData.append(`trailers[${index}].position`, position + 1);

        values.forEach(([name, value]) => {
            formData.append(`trailers[${index}].${name}`, value);
        });

        index++;
    });
};

/**
 * Poultry truck exit form.
 *
 * Complements the base information with the transport that leaves the plant,
 * the date on which it is expected to leave and the thermo trailers it pulls.
 * The movement is always an exit, so it is complemented by the arrival of the
 * vehicle and the user cannot select its direction.
 */
export const POULTRY_TRUCK_EXIT_FORM_MODEL = {
    ...PLANT_MATERIAL_CONTROL_BASE_MODEL,
    materialControlType: ENTRY_AND_EXIT_FORM_TYPE.POULTRY_TRUCK_EXIT,
    carrierCompany: '',
    tractor: '',
    dollyNo: '',
    driversName: '',
    licenseNo: '',
    expectedMovementDate: null,
    thermoTrailer1: { ...THERMO_TRAILER_MODEL },
    thermoTrailer2: { ...THERMO_TRAILER_MODEL },
    ...ARRIVAL_SECTION_DEFAULTS,
    /**
     * The transport and the thermo trailers are completed by the guard at the
     * gate, so the folio is registered with the date the truck is expected to
     * leave and the arrival of the vehicle.
     */
    isValid(payload) {
        return (
            PLANT_MATERIAL_CONTROL_BASE_MODEL.isValid.call(this, payload) &&
            this.expectedMovementDate !== null &&
            isValidSection(this, FIXED_ARRIVAL_SECTION)
        );
    },
    toRegister(payload) {
        const formData = PLANT_MATERIAL_CONTROL_BASE_MODEL.toRegister.call(this, payload);
        formData.append('movementDirection', FIXED_MOVEMENT_DIRECTION);
        appendEntryAndExitFormTransport(formData, { carrierCompany: this.carrierCompany });
        formData.append('expectedMovementDate', new Date(this.expectedMovementDate).getTime());
        appendOptionalTransportFields(formData, this, POULTRY_TRUCK_TRANSPORT_FIELDS);
        appendThermoTrailers(formData, this);
        appendSection(formData, this, FIXED_ARRIVAL_SECTION);
        return formData;
    },
};

/**
 * Default values of the fields captured for the selected movement direction.
 *
 * Exposed on its own so the platforms and the observations of the movement can
 * be restored whenever the user changes the direction of the movement, because
 * they describe the movement that is no longer selected.
 */
export const MOVEMENT_DIRECTION_FIELD_DEFAULTS = {
    platform1No: '',
    platform2No: '',
    observations: ''
};

/**
 * Live poultry truck form.
 *
 * Complements the base information with the direction of the movement, the
 * section it enables and the transport that carries the live poultry, which is
 * completed by the guard at the gate when the requester leaves it blank.
 */
export const LIVE_POULTRY_TRUCK_FORM_MODEL = {
    ...PLANT_MATERIAL_CONTROL_BASE_MODEL,
    materialControlType: ENTRY_AND_EXIT_FORM_TYPE.LIVE_POULTRY_TRUCK,
    tractor: '',
    dollyNo: '',
    driversName: '',
    licenseNo: '',
    movementDirection: null,
    ...MOVEMENT_DIRECTION_DATE_DEFAULTS,
    ...MOVEMENT_DIRECTION_SECTION_DEFAULTS,
    ...MOVEMENT_DIRECTION_FIELD_DEFAULTS,
    /**
     * The transport and the date of the movement are completed by the guard at
     * the gate, so the folio is registered with the direction of the movement
     * and the section it enables.
     */
    isValid(payload) {
        return (
            PLANT_MATERIAL_CONTROL_BASE_MODEL.isValid.call(this, payload) &&
            isValidMovementDirectionSection(this)
        );
    },
    toRegister(payload) {
        const formData = PLANT_MATERIAL_CONTROL_BASE_MODEL.toRegister.call(this, payload);
        appendMovementDirection(formData, this);
        appendMovementDirectionDate(formData, this);
        appendMovementDirectionSection(formData, this);
        appendOptionalTransportFields(formData, this, POULTRY_TRUCK_TRANSPORT_FIELDS);

        // The platforms the requester already knows travel with the folio, so
        // the gate only completes the ones left blank
        appendSanitizedString(formData, 'platform1No', this.platform1No);
        appendSanitizedString(formData, 'platform2No', this.platform2No);

        const observations = sanitizeString(this.observations);
        if (observations)
            formData.append('extraDetails', observations);

        return formData;
    },
};

/**
 * Maximum length the information of the vehicle accepts.
 *
 * The transport of the folio stores it in a column of its own, so what is
 * written here is what that column holds: a longer text is refused by the
 * service instead of being registered cut.
 */
export const VEHICLE_INFORMATION_LENGTH = 512;

/**
 * Maximum length each value of the transport accepts.
 *
 * The transport stores every value in a column of its own, so what is written
 * here is what that column holds: a longer text is refused by the service
 * instead of being registered cut.
 */
export const TRANSPORT_FIELD_LENGTH = {
    carrierCompany: 128,
    vehicleInformation: VEHICLE_INFORMATION_LENGTH,
    companyOrArea: 128,
    tractor: 64,
    trailer: 64,
    driversName: 128,
    sealNumber: 64,
    dollyNo: 64,
    licenseNo: 64,
    plateNo: 64,
    vehicleModel: 128
};

/**
 * Returns the length a value of the transport is captured with.
 *
 * @param {string} name - Name the form captures the value with.
 *
 * @returns {number} Length the column of that value holds.
 */
export const getTransportFieldLength = (name) => TRANSPORT_FIELD_LENGTH[name] || 128;

/**
 * Maximum length each value of a thermo trailer accepts.
 *
 * The trailer stores every value in a column of its own, the same way the
 * transport does, so a longer text is refused by the service.
 */
export const THERMO_TRAILER_FIELD_LENGTH = {
    destination: 128,
    thermoNo: 64,
    seal1No: 64,
    seal2No: 64,
    avisoCzmNo: 64,
    dispatchNoteNo: 64,
    notes: 512
};

export const ENTRY_AND_EXIT_FORMS = {
    GENERAL_ENTRY_EXIT: {
        index: 0,
        key: "GENERAL_ENTRY_EXIT",
        trans: "general_entry_exit",
        description: "register_the_entry_or_exit_of_materials_at_the_plant",
        Icon: IconExitDevice,
        formModel: GENERAL_ENTRY_EXIT_FORM_MODEL
    },
    PLANT_MATERIAL_CONTROL: {
        index: 1,
        key: "PLANT_MATERIAL_CONTROL",
        trans: "plant_material_control",
        description: "track_materials_leaving_the_plant_and_their_expected_return_when_applicable",
        Icon: IconSwapHorizontal,
        formModel: PLANT_MATERIAL_CONTROL_FORM_MODEL
    },
    CRATES_AND_PALLETS_RETURN: {
        index: 2,
        key: "CRATES_AND_PALLETS_RETURN",
        trans: "crates_and_pallets_return",
        description: "register_the_return_of_crates_lids_and_plastic_pallets_to_the_plant",
        Icon: IconPackageReturn,
        formModel: CRATES_AND_PALLETS_RETURN_FORM_MODEL
    },
    POULTRY_TRUCK_EXIT: {
        index: 3,
        key: "POULTRY_TRUCK_EXIT",
        trans: "poultry_truck_exit",
        description: "register_the_exit_of_vehicles_transporting_processed_poultry_from_the_plant",
        Icon: IconVehicleInMotion,
        formModel: POULTRY_TRUCK_EXIT_FORM_MODEL
    },
    LIVE_POULTRY_TRUCK: {
        index: 4,
        key: "LIVE_POULTRY_TRUCK",
        trans: "live_poultry_truck",
        description: "register_the_entry_and_exit_of_vehicles_transporting_live_poultry",
        Icon: IconPoultryTransportVehicle,
        formModel: LIVE_POULTRY_TRUCK_FORM_MODEL
    }
}

/**
 * Returns the configuration of the entry and exit form matching the given key.
 *
 * The forms that were registered before the type was captured are read as a
 * general entry and exit, which is the movement every plant registers.
 *
 * @param {string} formKey - Key of the form type.
 *
 * @returns {Object} Configuration of the form type.
 */
export const getEntryAndExitFormByKey = (formKey) =>
    ENTRY_AND_EXIT_FORMS[formKey] || ENTRY_AND_EXIT_FORMS.GENERAL_ENTRY_EXIT;

/**
 * Form key each material control type of the service is read as.
 *
 * The service names the return of crates and pallets without the conjunction,
 * so the type it sends is translated to the key the forms are declared with.
 */
const ENTRY_AND_EXIT_FORM_KEY_BY_TYPE = {
    GENERAL_ENTRY_EXIT: ENTRY_AND_EXIT_FORMS.GENERAL_ENTRY_EXIT.key,
    PLANT_MATERIAL_CONTROL: ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key,
    CRATES_PALLETS_RETURN: ENTRY_AND_EXIT_FORMS.CRATES_AND_PALLETS_RETURN.key,
    POULTRY_TRUCK_EXIT: ENTRY_AND_EXIT_FORMS.POULTRY_TRUCK_EXIT.key,
    LIVE_POULTRY_TRUCK: ENTRY_AND_EXIT_FORMS.LIVE_POULTRY_TRUCK.key,
}

/**
 * Returns the configuration of the entry and exit form matching the material
 * control type the service sends.
 *
 * @param {string} type - Type of the material control.
 *
 * @returns {Object} Configuration of the form type.
 */
export const getEntryAndExitFormByType = (type) =>
    getEntryAndExitFormByKey(ENTRY_AND_EXIT_FORM_KEY_BY_TYPE[type]);

/**
 * Request type a vehicle movement is read with in the requests tab.
 *
 * The movement is not registered with a form of entry and exit, but it is
 * reviewed through the same requests tab, so it carries its own label and icon.
 */
export const VEHICLE_MOVEMENT_REQUEST_TYPE = {
    key: VEHICLE_MOVEMENT_TYPE,
    trans: "vehicle_movement",
    Icon: IconVehicle
};

/**
 * Tells whether a folio of material control is a vehicle movement.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} True when the folio is a vehicle movement.
 */
export const isVehicleMovement = (item) => item?.type === VEHICLE_MOVEMENT_TYPE;

/**
 * Returns the configuration a request of the requests tab is read with.
 *
 * The requests tab lists the entry and exit forms and the vehicle movements, so
 * a vehicle movement is read with its own type and not as a general entry and exit.
 *
 * @param {string} type - Type of the material control.
 *
 * @returns {Object} Configuration of the request type.
 */
export const getMaterialControlRequestTypeByType = (type) =>
    type === VEHICLE_MOVEMENT_TYPE ? VEHICLE_MOVEMENT_REQUEST_TYPE : getEntryAndExitFormByType(type);

/**
 * Materials a folio of material control carries, read as the single list the
 * detail displays.
 *
 * The service keeps them in two blocks: the ones captured with a quantity and
 * a measurement unit, and the ones that are only described with the direction
 * they take. Both are read with the same name, and the evidence of a material
 * captured with a quantity is the last one the gate registered for it.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {Array} Materials of the folio.
 */
export const getMaterialControlItems = (item) => {
    // The note of the validation travels with the material and not with the
    // photo, so the evidence is read with the note the gate wrote for it
    const lastEvidence = (e) => {
        const evidence = (e?.evidence || []).slice(-1)[0] || null;

        return evidence
            ? {...evidence, note: e?.validationNote ?? evidence?.note ?? null}
            : evidence;
    };

    return [
        ...(item?.completeItems || []).map((e) => ({
            ...e,
            itemEvidence: lastEvidence(e)
        })),
        // A material the gate only describes carries no evidence of its own,
        // so it is read with the name the list displays it with
        ...(item?.basicItems || []).map((e) => ({
            ...e,
            name: e.description,
            itemEvidence: null
        }))
    ];
};

/**
 * Blocks of a folio the service keeps the history of, named as it stores them.
 *
 * The service writes a row every time a value of one of these blocks changes,
 * so the detail can read who completed each one of them and when.
 */
export const MATERIAL_CONTROL_AUDIT_ENTITY = {
    MATERIAL_CONTROL: 'material_control',
    TRANSPORT: 'material_control_transport',
    PACKAGING_QUANTITY: 'material_control_packaging_quantity',
    TRAILER: 'material_control_trailer',
    ITEM_BASIC: 'material_control_item_basic',
    ITEM_COMPLETE: 'material_control_item_complete'
};

/**
 * Reads the last change registered for a value of the folio.
 *
 * The history travels newest first, so the first change of the field is the one
 * that says who left the value the folio carries and when it was written; a
 * value that was never changed carries none.
 *
 * A block the folio keeps more than one record of — the materials it carries —
 * is read by the record the value belongs to, because the history names every
 * one of them apart.
 *
 * @param {Object} item - Folio being read.
 * @param {string} entityName - Block the value belongs to.
 * @param {string} fieldName - Field the value is read with.
 * @param {string} [entityId] - Record of the block the value belongs to.
 *
 * @returns {Object|null} Change of the field, or null when it has none.
 */
export const getFieldAudit = (item, entityName, fieldName, entityId = null) => (item?.fieldAudits || [])
    .find((e) => e.entityName === entityName
        && e.fieldName === (TRANSPORT_FIELD_NAMES[fieldName] || fieldName)
        && (entityId === null || e.entityId === entityId)) || null;

/**
 * User that registered a material of the folio, with the moment it was written.
 *
 * The materials are registered either with the folio or at the gate, so every
 * one of them keeps the user that wrote it; a material registered before that
 * user was kept falls back to the one the rest of the log is read with.
 *
 * @param {Object} material - Material being read.
 *
 * @returns {Object|null} User and moment of the material, or null when it has none.
 */
export const getMaterialRegisteredSource = (material) => (
    material?.createdBy
        ? {name: material.createdBy, date: material?.createdAt}
        : null
);

/**
 * Tells whether the gate still owes a movement of the folio.
 *
 * The folio declares the movements of the vehicle it waits for, so the one that
 * was demanded and never registered is still expected: the report of a
 * discrepancy does not replace it, and the log of that movement is not written
 * yet.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Returns true when a movement of the gate is still expected.
 */
export const owesGateMovement = (item) => (item?.gateRequirements || []).some((requirement) =>
    requirement?.required !== false
    && !(item?.gateEvents || []).some((e) => e?.type === requirement?.gateEventType)
);

/**
 * Icon the chip of a value registered on a folio is read with.
 *
 * The folio is registered by whoever asks for the movement and completed at the
 * gate, so a value the requester left written travels with the folio and is
 * read with the person that registered it, the same way the driver the
 * transport arrives with is; everything else was written at the gate, so it is
 * read with the guard that registered it there.
 *
 * @param {Object} item - Folio being read.
 * @param {string} name - User that wrote the value.
 *
 * @returns {Function} Icon of the user that registered the value.
 */
export const getRegisteredByIcon = (item, name) => {
    const isFolioUser = !name
        || name === item?.requestedBy
        || name === item?.transport?.driverName;

    return isFolioUser ? PersonOutlinedChip : IconGuardTitle;
};

/**
 * User a value the gate wrote is named after, with the moment it was written.
 *
 * Every value is named by whoever wrote it, so the user and the moment travel
 * together and the first source that names someone is the one the chip is read
 * with: the gate that confirmed the transport, the movement that closed the
 * folio, the last movement the gate registered while it is still open, and the
 * requester that registered the folio, because a value that never reached the
 * gate is the one that travelled with the request.
 *
 * @param {Object} item - Folio being read.
 * @param {number} [registeredAt] - Moment the gate closed the folio.
 *
 * @returns {Object} User and moment the value is read with.
 */
export const getGateRegisteredSource = (item, registeredAt = item?.dispatchDate ?? null) => {
    const lastGateEvent = (item?.gateEvents || []).slice(-1)[0] || null;

    return [
        {name: item?.transport?.registeredBy, date: registeredAt},
        {name: item?.dispatchUser, date: registeredAt},
        {name: lastGateEvent?.registeredBy, date: lastGateEvent?.eventDate},
        {name: item?.requestedBy, date: item?.createdAt}
    ].find((e) => Boolean(e.name)) || {name: null, date: null};
};

/**
 * Tells whether a value of the gate log was already registered.
 *
 * A quantity counted at the gate can be zero, which is a count of its own and
 * not a value left blank, so only what was never captured is read as pending.
 *
 * @param {*} value - Value registered for the row.
 *
 * @returns {boolean} Returns true when the row carries information.
 */
export const hasGateLogValue = (value) => value !== null && value !== undefined && value !== '';

/**
 * Values of the vehicle the transport of a folio is read with.
 */
const TRANSPORT_FIELDS = [
    'carrierCompany',
    'vehicleInformation',
    'companyOrArea',
    'tractor',
    'trailer',
    'sealNumber',
    'dollyNo',
    'licenseNo',
    'plateNo',
    'vehicleModel'
];

/**
 * Packaging of a crates and pallets return, named as the list of the detail
 * reads every one of them.
 */
export const PACKAGING_FIELD_BY_TYPE = {
    LARGE_BOX: 'largeBoxes',
    SMALL_BOX: 'smallBoxes',
    LID: 'lids',
    PALLET: 'pallets'
};

/**
 * Reads the record a packaging of the folio is counted in.
 *
 * Every packaging is kept as a record of its own, so the history of its count
 * is named by that record and not by the folio.
 *
 * @param {Object} item - Folio being read.
 * @param {string} field - Name the list reads the packaging with.
 *
 * @returns {Object|null} Record of the packaging, or null when the folio counts none.
 */
export const getPackagingQuantityRecord = (item, field) => (item?.packagingQuantities || [])
    .find((e) => PACKAGING_FIELD_BY_TYPE[e?.packagingType] === field) || null;

/**
 * Position every thermo trailer is registered with, named as the list of the
 * detail reads it.
 */
const THERMO_TRAILER_FIELD_BY_POSITION = {
    1: 'thermoTrailer1',
    2: 'thermoTrailer2'
};

/**
 * Gate log of a folio of material control, read as the single object the list
 * of the detail displays.
 *
 * The service keeps every block of the folio apart — the transport in an object
 * of its own, the thermo trailers in a list read by the position they are
 * pulled in, and the quantities of the crates in a list read by the packaging
 * they count — while the list declares its values flat, so the folio is read
 * once and every block is placed where the list looks for it.
 *
 * The values a block names differently are read with the name of the list. The
 * platforms are written on the folio itself, so they are read as it carries them.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {Object} Values of the gate log of the folio.
 */
export const getMaterialControlGateLog = (item) => {
    const transport = item?.transport || {};
    // The transport is registered as a record of its own, so only the values
    // of the vehicle are read and the identifiers of the block stay out of the
    // folio
    const vehicle = TRANSPORT_FIELDS.reduce((acc, name) => {
        acc[name] = transport[name];

        return acc;
    }, {});

    const trailers = (item?.trailers || []).reduce((acc, e) => {
        const field = THERMO_TRAILER_FIELD_BY_POSITION[e?.position];

        if (field) acc[field] = {...e, avisoCzmNo: e?.avisoCzm};

        return acc;
    }, {});

    // The packaging is counted once: the quantity the requester expected is the
    // one the folio carries and the guard writes the one that was left blank on
    // that same count, so the list reads a single quantity for every packaging
    const packaging = (item?.packagingQuantities || []).reduce((acc, e) => {
        const field = PACKAGING_FIELD_BY_TYPE[e?.packagingType];

        if (field)
            acc[field] = e?.expectedQuantity;

        return acc;
    }, {});

    // The movement is the one the folio was registered for, so its date is the
    // one it declares; a folio registered without it is read with the moment
    // the gate wrote when it dispatched the materials, because that is the
    // movement the group reads and not the arrival of the vehicle that is
    // registered after it
    const dispatchEvent = (item?.gateEvents || [])
        .filter((e) => e?.type === MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DISPATCHED])
        .slice(-1)[0] || null;
    const movementDate = item?.expectedMovementDate ?? dispatchEvent?.eventDate;

    return {
        ...vehicle,
        driversName: transport?.driverName,
        ...trailers,
        ...packaging,
        date: getDateFormat(movementDate, false),
        time: getTimeFormat(movementDate)
    };
};

/**
 * Folio of material control, read as the detail displays it.
 *
 * The service keeps apart what closed the folio — the movements the gate
 * registered, the report raised against it and the photos attached to it —
 * while the detail reads the result of the folio as a single block, so the
 * folio is read once and every value is placed where the view looks for it.
 */
export const MATERIAL_CONTROL_DETAIL_MODEL = {
    /**
     * Movement the gate registered to close the folio.
     *
     * @param {Object} item - Folio being read.
     *
     * @returns {Object|null} Dispatch of the folio, or null when it has none.
     */
    getDispatchEvent: function (item) {
        return (item?.gateEvents || [])
            .filter((e) => e.type === MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DISPATCHED])
            .slice(-1)[0] || null;
    },

    /**
     * Report the folio was closed with.
     *
     * A folio closed with a discrepancy is read with the report raised against
     * it, because that is where the guard wrote what was found.
     *
     * @param {Object} item - Folio being read.
     * @param {number} statusId - Status the folio is read in.
     *
     * @returns {Object|null} Report of the folio, or null when it carries none.
     */
    getDiscrepancyReport: function (item, statusId) {
        return statusId === STATUS_MODEL.DISCREPANCY
            ? (item?.reports || []).slice(-1)[0] || null
            : null;
    },

    /**
     * Photos the guard attached to the report.
     *
     * The report is read in the card that details the discrepancies, so the
     * photos travel with the folio; the movements that are not registered with
     * a form carry them on the folio itself.
     *
     * @param {Object} item - Folio being read.
     * @param {Object} report - Report the folio was closed with.
     *
     * @returns {Array} Files of the report.
     */
    getDiscrepancyFiles: function (item, report) {
        return (report?.evidence
            || report?.reportEvidence
            || item?.reportEvidence
            || [])
            .filter((e) => e?.fileUploadDto)
            .map((e) => FILE_MODEL.set(e));
    },

    /**
     * Notes the gate wrote for the folio.
     *
     * The notes travel with the gate log the guard fills while the folio waits
     * for the vehicle, so a dispatch registered once the arrival was already
     * written carries none, and the badge is read with the last ones the gate
     * left.
     *
     * @param {Object} item - Folio being read.
     *
     * @returns {string|null} Notes of the folio, or null when it carries none.
     */
    getLastGateNotes: function (item) {
        return (item?.gateEvents || [])
            .filter((e) => e?.notes)
            .slice(-1)[0]?.notes || null;
    },

    /**
     * Reads a folio of material control with the shape the detail displays it.
     *
     * The badge reads the result of the folio with the shape every movement of
     * material control carries, the materials of the two blocks the service
     * keeps them in are read as one list, and the gate log is read flat, so the
     * blocks the service keeps apart travel with the folio under the names the
     * list looks for.
     *
     * @param {Object} item - Folio read from the service.
     *
     * @returns {Object|null} Folio as the detail reads it.
     */
    toSet: function (item) {
        if (!item) return item;

        const statusId = getMaterialRequestTableStatusId(item);
        const dispatchEvent = this.getDispatchEvent(item);
        const discrepancyReport = this.getDiscrepancyReport(item, statusId);

        return {
            ...item,
            ...getMaterialControlGateLog(item),
            items: getMaterialControlItems(item),
            dispatchStatusId: statusId,
            dispatchReason: discrepancyReport?.comment
                || dispatchEvent?.notes
                || this.getLastGateNotes(item),
            dispatchDate: discrepancyReport?.createdAt ?? dispatchEvent?.eventDate ?? null,
            dispatchUser: discrepancyReport?.reportedBy || dispatchEvent?.registeredBy || null,
            discrepancyFiles: this.getDiscrepancyFiles(item, discrepancyReport)
        };
    }
};

/**
 * Reasons a movement of a vehicle is registered with.
 *
 * The reason says why the vehicle comes into the plant or leaves it, and it is
 * the one the movements tab reads to tell one movement from another.
 */
export const VEHICLE_MOVEMENT_REASONS = {
    MAINTENANCE: {
        key: "MAINTENANCE",
        trans: "maintenance"
    },
    VISIT: {
        key: "VISIT",
        trans: "visit"
    },
    INTERNAL_TRANSFER: {
        key: "INTERNAL_TRANSFER",
        trans: "internal_transfer"
    },
    INSPECTION: {
        key: "INSPECTION",
        trans: "inspection"
    },
    FUEL_SUPPLY: {
        key: "FUEL_SUPPLY",
        trans: "fuel_supply"
    }
}

/**
 * Directions a movement of a vehicle takes.
 *
 * The movement registers the vehicle itself and not the materials it carries,
 * so the directions are described by what the vehicle does.
 */
export const VEHICLE_MOVEMENT_DIRECTION_OPTIONS = MOVEMENT_DIRECTION_OPTIONS.map((option) => ({
    ...option,
    description: option.key === MOVEMENT_DIRECTION.ENTRY
        ? 'the_vehicle_comes_into_the_plant'
        : 'the_vehicle_leaves_the_plant',
    // The group of the gate log is named with the icon every form names it with,
    // which already carries the box it is read in
    gateIcon: option.key === MOVEMENT_DIRECTION.ENTRY
        ? IconArrowEntry
        : IconArrowExit
}));

/**
 * Movement of a vehicle, read as the detail displays it.
 *
 * The movement is closed by the gate the same way a folio registered with a
 * form is, so it is read with the shape every movement of material control
 * carries and the badge of the detail reads all of them alike.
 */
export const VEHICLE_MOVEMENT_DETAIL_MODEL = {
    /**
     * Direction the movement takes.
     *
     * The movement registers the vehicle itself and not the materials it
     * carries, so the direction is what tells the date the gate closed it with.
     *
     * @param {Object} item - Movement being read.
     *
     * @returns {Object|null} Direction of the movement, or null when it has none.
     */
    getDirectionOption: function (item) {
        // The service stores the direction as text, so the option is matched by
        // the value it is registered with and not by the key the form selects
        return VEHICLE_MOVEMENT_DIRECTION_OPTIONS
            .find((e) => e.value === item?.movementDirection) || null;
    },

    /**
     * Gate event the moment of the movement was registered with.
     *
     * The guard writes the moment the vehicle came into the plant or left it
     * along with the dispatch, so the last dispatch is the one that carries it.
     *
     * @param {Object} item - Movement being read.
     *
     * @returns {Object|null} Dispatch event of the movement, or null when the gate has not registered it.
     */
    getMovementEvent: function (item) {
        return MATERIAL_CONTROL_DETAIL_MODEL.getDispatchEvent(item);
    },

    /**
     * Reads a movement of a vehicle from the service with the names the detail
     * reads it by.
     *
     * The service keeps the vehicle in the transport block, the result of the
     * gate in its events and reports, and the return of the vehicle in the
     * requirements of the folio, while the detail and the gate log of the guard
     * read all of them on the movement itself, so the movement is read once and
     * every block is placed where the views look for it.
     *
     * @param {Object} data - Movement read from the service.
     *
     * @returns {Object|null} Movement as the detail reads it.
     */
    fromService: function (data) {
        if (!data) return null;

        const transport = data?.transport || {};
        // The status is read the same way every folio of material control is:
        // the approval until it is approved, and the gate once it is
        const statusId = getMaterialRequestTableStatusId(data);
        const dispatchEvent = MATERIAL_CONTROL_DETAIL_MODEL.getDispatchEvent(data);
        // A movement reported at the gate is read with the report the guard raised
        const report = MATERIAL_CONTROL_DETAIL_MODEL.getDiscrepancyReport(data, statusId);

        return {
            ...data,
            dispatchStatusId: statusId,
            companyOrArea: transport.companyOrArea,
            plateNo: transport.plateNo,
            vehicleModel: transport.vehicleModel,
            driversName: transport.driverName,
            licenseNo: transport.licenseNo,
            gateRegisteredBy: report?.reportedBy || dispatchEvent?.registeredBy || null,
            dispatchNote: report?.comment
                || dispatchEvent?.notes
                || MATERIAL_CONTROL_DETAIL_MODEL.getLastGateNotes(data),
            reportedAt: report?.createdAt ?? null,
            reportEvidence: report?.evidence || []
        };
    },

    /**
     * Photos the guard attached to the report.
     *
     * A movement is not registered with a form, so the evidence of the report
     * travels on the movement itself.
     *
     * @param {Object} item - Movement being read.
     *
     * @returns {Array} Files of the report.
     */
    getDiscrepancyFiles: function (item) {
        return (item?.reportEvidence || []).map((e) => FILE_MODEL.set(e));
    },

    /**
     * Reads a movement of a vehicle with the shape the detail displays it.
     *
     * The dispatch is what closes the movement, and the date it was closed with
     * is the one of the direction it takes, because a vehicle that comes into
     * the plant is closed with its arrival and the one that leaves it with its
     * departure.
     *
     * @param {Object} item - Movement read from the service.
     *
     * @returns {Object|null} Movement as the detail reads it.
     */
    toSet: function (item) {
        if (!item) return null;

        const movementEvent = this.getMovementEvent(item);

        return {
            ...item,
            dispatchReason: item?.dispatchNote,
            // A movement reported at the gate is closed by its report, so the
            // result is read with the moment it was raised and with the guard
            // that wrote it; the movement that was not reported is read with
            // the one the gate registered it with
            dispatchDate: item?.reportedAt ?? movementEvent?.eventDate ?? null,
            dispatchUser: item?.gateRegisteredBy || movementEvent?.registeredBy || null,
            discrepancyFiles: this.getDiscrepancyFiles(item)
        };
    }
};

/**
 * Fields of the vehicle that makes the movement, empty as the requester leaves
 * them, because what is not captured is completed by the guard at the gate.
 */
export const VEHICLE_MOVEMENT_VEHICLE_DEFAULTS = {
    companyOrArea: '',
    plateNo: '',
    vehicleModel: '',
    driversName: '',
    licenseNo: ''
}

/**
 * Sections the information card of a movement of a vehicle displays.
 *
 * A movement is not registered with a type of form, so it declares the rows it
 * is read with instead of taking them from the folio.
 *
 * @param {Object} item - Movement being read.
 *
 * @returns {Object} Sections the card displays for it.
 */
export const getVehicleMovementDetailsSections = (item) => {
    return {
        Icon: IconVehicle,
        headerIcon: IconDocumentStaggeredLines,
        movementDirection: item?.movementDirection,
        showMovementType: true,
        // The reason is read as its own row, so the direction is named after the
        // movement itself and not after its type
        movementTypeTrans: 'movement',
        // The movement is what the vehicle does, so it is read right after the
        // areas that follow the folio and before the values declared for it
        movementTypeBeforeFields: true,
        fields: [
            {title: 'type', name: 'movementTypeName'},
            {title: 'extra_details', name: 'extraDetails'}
        ],
        showEvidence: true,
        // The dispatch is the movement of the vehicle itself, so the record it
        // writes is read as the entry of the trip when the unit comes into the
        // plant and as its exit when it leaves, the same way a plant material
        // control reads the movement of its materials
        showTripExit: true,
        tripByMovementDirection: true,
        // The notes are read as the extra details of the movement, so the folio
        // does not repeat them
        showNotes: false,
        showArrivalRecord: true
    };
};

/**
 * Values of the vehicle read in the gate log of a movement, paired the way the
 * detail displays them. Every one of them says who registered it, because the
 * requester leaves blank what the guard completes at the gate.
 */
export const VEHICLE_MOVEMENT_TRANSPORT_ROWS = [
    {trans: 'plate_no', field: 'plateNo', registered: true},
    {trans: 'vehicle_model', field: 'vehicleModel', registered: true},
    {trans: 'driver_s_name', field: 'driversName', registered: true},
    {trans: 'license_no', field: 'licenseNo', registered: true}
];

/**
 * Values the guard completes at the gate: the vehicle that makes the movement,
 * the moment it comes in or goes out and the notes of what was observed.
 */
export const VEHICLE_MOVEMENT_GATE_DEFAULTS = {
    ...VEHICLE_MOVEMENT_VEHICLE_DEFAULTS,
    ...MOVEMENT_DIRECTION_DATE_DEFAULTS,
    notes: ''
}

/**
 * Fields of the transport of a movement of a vehicle.
 *
 * The requester registers the vehicle with what it knows of it, so every field
 * is optional and the ones left blank are completed by the guard at the gate.
 */
const VEHICLE_MOVEMENT_TRANSPORT_FIELDS = Object.keys(VEHICLE_MOVEMENT_VEHICLE_DEFAULTS);

/**
 * Movement of a vehicle.
 *
 * A movement registers the vehicle itself and not the materials it carries, so
 * it is captured with the reason it is made for, the direction it takes and the
 * return of the vehicle the folio stays open until. The transport and the
 * moment of the movement are completed by the guard at the gate, so they are
 * not asked for from the requester.
 */
export const VEHICLE_MOVEMENT_FORM_MODEL = {
    id: null,
    title: '',
    code: '',
    requestedById: null,
    movementType: '',
    extraDetails: '',
    movementDirection: null,
    ...VEHICLE_MOVEMENT_GATE_DEFAULTS,
    ...MOVEMENT_DIRECTION_SECTION_DEFAULTS,
    items: [],
    evidence: [],
    /**
     * The movement is registered with the reason it is made for, the direction
     * it takes and the documents that support it. The return of the vehicle is
     * asked for by the section of the direction, because a vehicle that comes
     * into the plant is awaited to leave it and one that leaves it to come back.
     */
    isValid({ photoPicker, selectedEmployees, watchers, responsibleDepartments }) {
        return (
            isNotEmpty(this.title) &&
            isNotEmpty(this.code) &&
            isNotEmpty(this.movementType) &&
            this.movementDirection !== null &&
            isValidMovementDirectionSection(this) &&
            isValidEntryAndExitFormPeople({ selectedEmployees, watchers, responsibleDepartments }) &&
            photoPicker?.listFiles?.length > 0
        );
    },
    /**
     * A movement does not carry a list of materials, so there is nothing to
     * validate for it.
     */
    isValidItems() {
        return true;
    },
    toRegister({ selectedEmployees, watchers, responsibleDepartments, files }) {
        const formData = new FormData();
        formData.append('type', VEHICLE_MOVEMENT_TYPE);
        formData.append('title', sanitizeString(this.title));
        formData.append('code', sanitizeString(this.code));
        formData.append('movementReason', this.movementType);
        appendEntryAndExitFormPeople(formData, { selectedEmployees, watchers, responsibleDepartments });
        appendMovementDirection(formData, this);
        // The date of the movement is the one of the direction it takes, and it
        // is only sent when the requester declared it
        appendMovementDirectionDate(formData, this);
        appendOptionalTransportFields(formData, this, VEHICLE_MOVEMENT_TRANSPORT_FIELDS);
        appendMovementDirectionSection(formData, this);
        appendEntryAndExitFormEvidence(formData, files);
        appendSanitizedString(formData, 'extraDetails', this.extraDetails);
        // The notes of the direction travel with the folio, so the guard only
        // completes them when the requester left them blank
        appendSanitizedString(formData, 'movementNotes', this.notes);
        return formData;
    },
};

/**
 * Returns the configuration of the reason matching the given key.
 *
 * @param {string} reasonKey - Key of the reason of the movement.
 *
 * @returns {Object|null} Configuration of the reason, null when it is not one
 * of the reasons the movements are registered with.
 */
export const getVehicleMovementReasonByKey = (reasonKey) =>
    VEHICLE_MOVEMENT_REASONS[reasonKey] || null;

/**
 * Translation key of each type of material control the service sends.
 *
 * The advanced search offers the type as the text the service stores, so it is
 * read with the label of the form or of the vehicle movement it belongs to.
 */
export const MATERIAL_CONTROL_TYPE_TRANS = Object.fromEntries(
    [...Object.keys(ENTRY_AND_EXIT_FORM_KEY_BY_TYPE), VEHICLE_MOVEMENT_TYPE]
        .map((type) => [type, getMaterialControlRequestTypeByType(type).trans])
);

/**
 * Translation key of each movement direction the service sends.
 */
export const MOVEMENT_DIRECTION_TRANS = Object.fromEntries(
    MOVEMENT_DIRECTION_OPTIONS.map(({value, trans}) => [value, trans])
);

/**
 * Translation key of each reason of a vehicle movement the service sends.
 */
export const VEHICLE_MOVEMENT_REASON_TRANS = Object.fromEntries(
    Object.values(VEHICLE_MOVEMENT_REASONS).map(({key, trans}) => [key, trans])
);


/**
 * Material request model.
 *
 * A request only asks for the materials, so it captures the title, the folio,
 * the employee that requests it, the vehicle that will move the materials and
 * the evidence of the request. The movement direction, its dates and the people
 * that follow the movement belong to the entry and exit forms, so they are not
 * captured or validated here.
 */
export const MATERIAL_REQUEST_MODEL = {
    id: null,
    title: '',
    code: '',
    requestedById: null,
    vehicleInformation: '',
    items: [GENERAL_ENTRY_EXIT_ITEM_MODEL],
    evidence: [],
    isValid({ photoPicker }) {
        return (
            isNotEmpty(this.title) &&
            isNotEmpty(this.code) &&
            photoPicker?.listFiles?.length > 0
        );
    },
    isValidItems() {
        return this.items.filter(e => !e.isValid()).length === 0;
    },
    /**
     * A request is registered by the material control service, the same one
     * every entry and exit form travels through, so it is written as the
     * general entry and exit, which is the movement it asks the materials for.
     *
     * The direction of the movement, its date and the people that follow it are
     * asked for by the forms and not by the request, so they are not sent and
     * the folio is left waiting for its dispatch.
     */
    toRegister({ selectedEmployees, files }) {
        const formData = new FormData();
        formData.append('type', ENTRY_AND_EXIT_FORM_TYPE.GENERAL_ENTRY_EXIT);
        formData.append('title', sanitizeString(this.title));
        formData.append('code', sanitizeString(this.code));
        appendEntryAndExitFormPeople(formData, { selectedEmployees });
        appendEntryAndExitFormTransport(formData, { vehicleInformation: this.vehicleInformation });
        appendEntryAndExitFormEvidence(formData, files);

        this.items.forEach((e, index) => {
            formData.append(`completeItems[${index}].name`, sanitizeString(e.name));
            formData.append(`completeItems[${index}].quantity`, e.quantity);
            formData.append(`completeItems[${index}].measurementUnitId`, e.measurementUnitId);
        });
        return formData;
    },
};

/**
 * Material request approval model.
 *
 * Represents approval information for
 * material request approval workflows.
 */
export const MATERIAL_REQUEST_APPROVAL_MODEL = {
    id: null,
    approvalStatusId:null,
    approvalReason: null,
    toRegister: ({ id, approvalStatusId, approvalReason}) => {
        return {
            id: id,
            approvalStatusId: approvalStatusId,
            approvalReason: approvalReason,
        }
    }
}

/**
 * Gate movements the material control service registers.
 *
 * The gate writes the movement it read, and the status the view closes the
 * folio with is what says which one of them it was.
 */
/**
 * Notice a folio of material control waits with, read by the status the gate
 * left it in.
 *
 * The two movements are waited for at opposite ends of the dispatch: a folio
 * that asks for the arrival of the vehicle is not dispatched until the guard
 * registers it, while the departure is only waited for once the folio was
 * already dispatched, and it is the movement that completes it.
 *
 * Every module of material control is closed by the same gate, so all of them
 * read the notice from here.
 */
export const PENDING_GATE_EVENT_TRANS = {
    [STATUS_MODEL.PENDING_ARRIVAL]: 'arrival_registration_is_required_before_the_dispatch_can_proceed',
    [STATUS_MODEL.PENDING_DEPARTURE]: 'waiting_for_the_vehicle_to_depart_the_request_will_be_completed_once_the_departure_is_recorded'
};

/**
 * Returns the notice the folio waits with, or null when it waits for nothing
 * at the gate.
 *
 * @param {number} statusId - Status the folio is read in.
 *
 * @returns {string|null} Translation key of the notice.
 */
export const getPendingGateEventTrans = (statusId) => PENDING_GATE_EVENT_TRANS[statusId] || null;

export const MATERIAL_CONTROL_GATE_EVENT_TYPE = {
    [STATUS_MODEL.ARRIVAL_RECORDED]: 'ARRIVAL',
    [STATUS_MODEL.DEPARTURE_RECORDED]: 'DEPARTURE',
    [STATUS_MODEL.DISPATCHED]: 'DISPATCH',
}

/**
 * Movement the gate has to register, read by the status the folio waits with.
 *
 * The gate writes the arrival of the vehicle and its departure, so the movement
 * the guard registers is the one the folio is waiting for.
 */
export const MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE = {
    [STATUS_MODEL.PENDING_ARRIVAL]: MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.ARRIVAL_RECORDED],
    [STATUS_MODEL.PENDING_DEPARTURE]: MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DEPARTURE_RECORDED],
}

/**
 * Body the validation of a material of a folio is sent with.
 *
 * The material travels with the name it is read with, and the evidence of the
 * validation travels as multipart, because it is the photo the guard takes at
 * the gate.
 *
 * A material that does not match what was declared is closed with the report of
 * the folio, so what is registered from the list is the material that matches.
 */
export const MATERIAL_CONTROL_ITEM_VALIDATION_MODEL = {
    toRegister({item, note, photoPicker, userId}) {
        const formData = new FormData();
        formData.append('id', item?.id);
        formData.append('materialControlId', item?.materialControlId);
        // A missing user is left out, so the service answers that it is required
        if (userId) formData.append('userId', userId);
        formData.append('validationStatusId', STATUS_MODEL.VALIDATED);
        appendSanitizedString(formData, 'validationNote', note);

        formData.append('name', sanitizeString(item?.name));

        // The photo the guard takes at the gate is uploaded whether it was
        // captured with the camera or picked from the computer, and the
        // evidence already registered is only read by the picker, so the one
        // that is not replaced does not travel again
        const isNewPhoto = Boolean(photoPicker?.fileSelected) || photoPicker?.id == null;
        const aux = isNewPhoto ? normalizePhotoPickerFile(photoPicker, MIME_TYPE.PNG) : null;

        if (aux) {
            formData.append('evidence[0].fileUploadDto.files', aux);
            formData.append('evidence[0].fileUploadDto.userFileNames', aux?.name);
        }

        return formData;
    }
};

/**
 * Body the gate movement of a material control is sent with.
 *
 * The service receives the movement as multipart, because the photos the guard
 * takes at the gate travel with it.
 */
export const MATERIAL_CONTROL_GATE_EVENT_MODEL = {
    materialControlId: null,
    type: null,
    eventDate: null,
    notes: null,
    toRegister: ({materialControlId, type, eventDate, notes, files, userId}) => {
        const formData = new FormData();
        formData.append('materialControlId', materialControlId);
        formData.append('type', type);
        // A missing user is left out, so the service answers that it is required
        if (userId) formData.append('userId', userId);

        // The movement that declares no date of its own is stamped by the
        // service with the moment it was registered, because the gate is where
        // it happens and the clock of the service is the one every record of
        // the folio is written with
        if (eventDate) formData.append('eventDate', eventDate);

        appendSanitizedString(formData, 'notes', notes);

        appendEntryAndExitFormEvidence(formData, files);

        return formData;
    }
}

/**
 * Body the discrepancy report of a material control is sent with.
 *
 * A folio reported at the gate is not dispatched: the report is what carries
 * what the guard found, and the evidence of it travels along.
 */
export const MATERIAL_CONTROL_REPORT_MODEL = {
    materialControlId: null,
    comment: null,
    toRegister: ({materialControlId, comment, files, userId}) => {
        const formData = new FormData();
        formData.append('materialControlId', materialControlId);
        // A missing user is left out, so the service answers that it is required
        if (userId) formData.append('userId', userId);
        appendSanitizedString(formData, 'comment', comment);
        appendEntryAndExitFormEvidence(formData, files);

        return formData;
    }
}

/**
 * Body the approval of a material control is sent with.
 *
 * The service answers with the records of the status table, the same ones the
 * table works with, so the status travels as it is.
 */
export const MATERIAL_CONTROL_APPROVAL_MODEL = {
    materialControlId: null,
    approvalStatusId: null,
    reason: null,
    toRegister: ({ id, approvalStatusId, approvalReason }) => {
        return {
            materialControlId: id,
            approvalStatusId: approvalStatusId,
            reason: approvalReason,
        }
    }
}

/**
 * Material request dispatch model.
 *
 * Represents dispatch or discrepancy report
 * information for material requests.
 */
export const MATERIAL_REQUEST_DISPATCH_MODEL = {
    id: null,
    dispatchStatusId:null,
    dispatchReason: null,
    toRegister: ({ id, dispatchStatusId, dispatchReason}) => {
        return {
            id: id,
            dispatchStatusId: dispatchStatusId,
            dispatchReason: dispatchReason,
        }
    }
}

/**
 * Handles material request approval actions.
 *
 * Sends approval information to backend service
 * and displays success or error notifications.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} navigate - Navigation handler.
 * @param {number|string} id - Material request identifier.
 * @param {string} comment - Approval comment.
 * @param {number} status - Approval status identifier.
 * @param {Function} setModified - State updater for refresh control.
 *
 * @returns {void}
 */
export const handleOnClick = (dispatch, navigate, id, comment, status, setModified) => {
    const body = {
        id,
        approvalStatusId: status,
        reason: comment
    };

    dispatch(ApprovalMaterialRequest(body)).then(({ payload: { data: { success, code } } }) => {
        if (success) {
            setModified(true);
            toast.success(i18next.t(getToastMessageByStatus(status)));
        } else {
            errorMsg(code);
        }
    });
}

/**
 * Model that maps submission request statuses to UI configuration.
 *
 * Includes:
 * - Labels
 * - Toast messages
 * - Styles (colors, classes)
 * - Translation keys
 */
export const MATERIAL_REQUEST_CARD_MODEL = {
    APPROVED: {
        id: STATUS_MODEL.APPROVED,
        title : 'approved',
        toast: "approved_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "approved_by"
    },
    DENIED: {
        id: STATUS_MODEL.DENIED,
        title : 'denied',
        toast: "denied_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
        byTrans: "denied_by"
    },
    CHANGES_REQUESTED: {
        id: STATUS_MODEL.CHANGES_REQUESTED,
        title : 'changes_requested',
        toast: "changes_requested_by",
        classCard: "card-warning-150-50",
        fontColor: "font-color-warning-700",
        infoColor: "icon-color-warning-700",
        byTrans: "changes_requested_by"
    },
    DISPATCHED: {
        id: STATUS_MODEL.DISPATCHED,
        title : 'dispatched',
        toast: "dispatched_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "dispatched_by"
    },
    DISCREPANCY: {
        id: STATUS_MODEL.DISCREPANCY,
        title : 'discrepancy_reported',
        // The report is what closes the folio, so the badge names it with the
        // icon of the review it waits for
        Icon: IconCircleCross,
        toast: "discrepancy_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
        byTrans: "discrepancy_by"
    },
    PENDING_ARRIVAL: {
        id: STATUS_MODEL.PENDING_ARRIVAL,
        title : 'PENDING_ARRIVAL',
        toast: "",
        classCard: "card-warning-150-50",
        fontColor: "font-color-warning-700",
        infoColor: "icon-color-warning-700",
        byTrans: ""
    },
    // The vehicle the folio waits for is read the same way it is awaited to
    // come in, because both movements keep the folio open at the gate
    PENDING_DEPARTURE: {
        id: STATUS_MODEL.PENDING_DEPARTURE,
        title : 'PENDING_DEPARTURE',
        toast: "",
        classCard: "card-warning-150-50",
        fontColor: "font-color-warning-700",
        infoColor: "icon-color-warning-700",
        byTrans: ""
    },
    // The movement the gate registered closes the folio, so it is named with
    // the guard that wrote it the same way the dispatch is
    ARRIVAL_RECORDED: {
        id: STATUS_MODEL.ARRIVAL_RECORDED,
        title : 'ARRIVAL_RECORDED',
        toast: "arrival_recorded_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "arrival_recorded_by"
    },
    DEPARTURE_RECORDED: {
        id: STATUS_MODEL.DEPARTURE_RECORDED,
        title : 'DEPARTURE_RECORDED',
        toast: "departure_recorded_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "departure_recorded_by"
    },
    /**
     * Retrieves configuration by status ID.
     *
     * @param {number|string} statusId - Status identifier
     * @returns {Object|null} Matching configuration or null if not found
     */
    getById: function (statusId) {
        const row = Object.entries(MATERIAL_REQUEST_CARD_MODEL).find(([ key, data]) => data?.id === parseInt(statusId));
        return row?.[1] || null;
    }
}

export const RECORD_DELIVERY_MODEL ={
    id: null,
    note: null,
    materialRequestItemId: null,
    fileUploadDto: null,
    set(data) {
        return {
            ...this,
            id: data?.itemEvidence?.id || null,
            note: data?.validationNote ?? data?.itemEvidence?.note ?? null,
            materialRequestItemId: data?.id || null,
            fileUploadDto: null,
        }
    },
    isValid({ photoPicker }) {
        // The evidence already registered is read by the picker, so the photo
        // is asked for again when it is deleted, and not taken as uploaded
        // because the material carries the id of the one it was closed with
        return (
            isNotEmpty(this.note) &&
            (photoPicker?.id != null || getPhotoPicker(photoPicker) !== null)
        );
    },
    toRegister({ file }) {
        let aux = null;

        const formData = new FormData();
        if(this.id) formData.append('id', this.id );
        appendSanitizedString(formData, 'note', this.note);
        formData.append('materialRequestItemId', this.materialRequestItemId);

        if(!file.id){
            aux =  normalizePhotoPickerFile(file, MIME_TYPE.PNG);
            formData.append('fileUploadDto.files', aux);
            formData.append('fileUploadDto.userFileNames', aux?.name);
        }else{
            formData.append('fileUploadDto.id', file.id);
        }

        return formData;
    },
    clear() {
        return {
            ...this,
            id: null,
            note: null,
            fileUploadDto: null,
        }
    },
    clearAll() {
        return {
            ...this,
            id: null,
            note: null,
            materialRequestItemId: null,
            fileUploadDto: null,
        }
    },
}

/**
 * Determines whether dispatch badge should be displayed.
 *
 * Badge is hidden for:
 * - Pending approval
 * - Pending dispatch
 *
 * @param {number} dispatchStatusId - Dispatch status identifier.
 *
 * @returns {boolean} Returns true if badge should be visible.
 */
export const showDispatchBadge = (dispatchStatusId) => (
    dispatchStatusId &&
    ![
        STATUS_MODEL.PENDING_TO_APPROVE,
        STATUS_MODEL.PENDING_TO_DISPATCH
    ].includes(dispatchStatusId)
);

/**
 * Retrieves the status identifier displayed
 * for a material request.
 *
 * Rules:
 * - Returns approval status if request is denied.
 * - Otherwise returns dispatch status.
 *
 * @param {Object} item - Material request information.
 *
 * @returns {number|null} Status identifier to display.
 */
export const getMaterialRequestStatusId = (item) => (
    item?.approvalStatusId === STATUS_MODEL.DENIED
        ? item?.approvalStatusId
        : item?.dispatchStatusId
);

/**
 * Retrieves the status identifier read in the tables of material control.
 *
 * Rules:
 * - Returns the status of the folio once it is approved, since from that point
 *   the folio is followed by its own status and not by the decision. A folio
 *   already read by a detail carries that status as dispatchStatusId.
 * - Otherwise returns the approval status, that is the decision it waits for.
 * - Falls back to the approval status when the folio is not returned with one.
 *
 * @param {Object} item - Row read from the table.
 *
 * @returns {number|null} Status identifier to display.
 */
export const getMaterialRequestTableStatusId = (item) => (
    item?.approvalStatusId === STATUS_MODEL.APPROVED
        ? (item?.statusId ?? item?.dispatchStatusId ?? item?.approvalStatusId)
        : item?.approvalStatusId
);

/**
 * Statuses a folio of material control still waits for a movement of the gate in.
 *
 * The QR code identifies the folio at the gate, so it is only offered while the
 * gate still has something to register on it. Mirrors the statuses the service
 * issues the QR code for.
 */
export const MATERIAL_CONTROL_QR_CODE_STATUS = [
    STATUS_MODEL.PENDING_ARRIVAL,
    STATUS_MODEL.PENDING_TO_DISPATCH,
    STATUS_MODEL.PENDING_DEPARTURE
];

/**
 * Tells whether the QR code of a folio of material control can be downloaded.
 *
 * @param {Object} item - Row read from the table.
 *
 * @returns {boolean} True when the folio is approved and the gate still has to register it.
 */
export const hasMaterialControlQrCode = (item) => (
    item?.approvalStatusId === STATUS_MODEL.APPROVED
    && MATERIAL_CONTROL_QR_CODE_STATUS.includes(item?.statusId ?? item?.dispatchStatusId)
);

/**
 * Retrieves the QR code of a folio of material control and downloads it as a
 * PNG file.
 *
 * @async
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} item - Row read from the table.
 *
 * @returns {void}
 */
export const downloadMaterialControlQrCode = async (dispatch, item) => {
    const {payload} = await dispatch(GetMaterialControlQrCode(item?.id));
    const {data, success, code} = payload?.data || {};

    if (!success) {
        errorMsg(code);
        return;
    }

    try {
        const image = await QRCode.toDataURL(JSON.stringify(data), {width: 300});
        const link = document.createElement('a');
        link.href = image;
        link.download = `${i18next.t('qr_code')}_${item?.code}.png`;
        link.click();
    } catch (err) {
        toast.error(i18next.t('something_went_wrong_2'));
    }
};

/**
 * Determines the dispatch status identifier
 * for pending dispatch validation.
 *
 * Rules:
 * - Returns null when request is already:
 *   - Dispatched
 *   - Reported as discrepancy
 * - Otherwise returns pending status.
 *
 * @param {number} dispatchStatusId - Current dispatch status identifier.
 *
 * @returns {number|null} Dispatch validation status.
 */
export const getMaterialRequestDispatchStatusId = (dispatchStatusId) => (
    [STATUS_MODEL.DISPATCHED, STATUS_MODEL.DISCREPANCY].includes(dispatchStatusId)
        ? null
        : STATUS_MODEL.PENDING
);

/**
 * Statuses a folio carries once it dispatched its materials.
 *
 * The evidence of a material is a draft of the gate until the dispatch is
 * registered, so a folio that was reported with discrepancies never dispatched
 * them and is not read here.
 */
export const MATERIAL_CONTROL_DISPATCHED_STATUS = [
    STATUS_MODEL.DISPATCHED,
    STATUS_MODEL.PENDING_DEPARTURE,
    STATUS_MODEL.DEPARTURE_RECORDED
];

/**
 * Whether the folio already dispatched its materials.
 *
 * The status says it while the dispatch is the last thing registered on the
 * folio, but a folio reported with discrepancies after it was dispatched is
 * read with the report and no longer with the dispatch, so the movement the
 * gate registered is what says the materials were already handed over.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the materials were dispatched.
 */
export const wasMaterialControlDispatched = (item) => (
    MATERIAL_CONTROL_DISPATCHED_STATUS.includes(item?.dispatchStatusId)
    || (item?.gateEvents || []).some(
        (e) => e?.type === MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DISPATCHED]
    )
);

/**
 * Text a value the gate never wrote is read with.
 *
 * A folio that is still open waits for the guard to complete it, so what is
 * blank says that it is pending; once the materials were dispatched nobody
 * writes on that movement any more, so what was left blank is read as a value
 * the folio does not carry.
 *
 * @param {Object} item - Folio being read.
 * @param {Function} t - Translation handler.
 *
 * @returns {string} Text the blank value is read with.
 */
export const getGateLogEmptyText = (item, t) => (
    wasMaterialControlDispatched(item) ? EMPTY_VALUES.DEFAULT : t('pending')
);
